import { useState } from "react";
import { X, Plus, Minus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const AddOnsModal = ({ isOpen, onClose, onConfirm, baseAmount = 100, serviceName = "Service" }) => {
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [destinationFee, setDestinationFee] = useState(0);
  const [tattooAreas, setTattooAreas] = useState(1);

  const addOnsOptions = [
    {
      id: "early_morning",
      name: "Early Morning Service",
      description: "Services starting before 8:00 AM",
      price: 50,
      type: "fixed"
    },
    {
      id: "tattoo_cover",
      name: "Tattoo Cover",
      description: "Professional tattoo coverage",
      price: 75,
      type: "quantity",
      unit: "per area"
    },
    {
      id: "destination",
      name: "Destination Location Fee",
      description: "Travel outside Las Vegas metro area",
      price: 0,
      type: "custom"
    }
  ];

  const handleToggleAddOn = (addOnId) => {
    if (selectedAddOns.includes(addOnId)) {
      setSelectedAddOns(selectedAddOns.filter(id => id !== addOnId));
    } else {
      setSelectedAddOns([...selectedAddOns, addOnId]);
    }
  };

  const calculateTotal = () => {
    let total = baseAmount;
    
    selectedAddOns.forEach(addOnId => {
      const addOn = addOnsOptions.find(a => a.id === addOnId);
      if (addOn.type === "fixed") {
        total += addOn.price;
      } else if (addOn.type === "quantity" && addOnId === "tattoo_cover") {
        total += addOn.price * tattooAreas;
      } else if (addOn.type === "custom" && addOnId === "destination") {
        total += parseFloat(destinationFee) || 0;
      }
    });
    
    return total;
  };

  const handleConfirm = () => {
    const addOnsDetails = selectedAddOns.map(addOnId => {
      const addOn = addOnsOptions.find(a => a.id === addOnId);
      let price = addOn.price;
      let details = addOn.name;
      
      if (addOn.type === "quantity" && addOnId === "tattoo_cover") {
        price = addOn.price * tattooAreas;
        details = `${addOn.name} (${tattooAreas} ${tattooAreas > 1 ? 'areas' : 'area'})`;
      } else if (addOn.type === "custom" && addOnId === "destination") {
        price = parseFloat(destinationFee) || 0;
      }
      
      return {
        id: addOnId,
        name: addOn.name,
        description: addOn.description,
        price,
        details
      };
    });
    
    onConfirm({
      addOns: addOnsDetails,
      total: calculateTotal()
    });
  };

  const total = calculateTotal();
  const addOnsTotal = total - baseAmount;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="addons-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Customize Your Service</DialogTitle>
          <p className="text-[#8C8C8C] mt-2">
            {serviceName} - Add any special services or customizations
          </p>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          {/* Base Service */}
          <div className="bg-[#F2EBE5] p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold text-lg">Base Deposit (20%)</p>
                <p className="text-sm text-[#8C8C8C]">Non-refundable deposit to secure your date</p>
              </div>
              <span className="text-2xl font-bold text-[#D4AF37]">${baseAmount.toFixed(2)}</span>
            </div>
          </div>

          {/* Add-Ons Selection */}
          <div>
            <h3 className="text-xl font-bold mb-4">Available Add-Ons</h3>
            <div className="space-y-4">
              {addOnsOptions.map(addOn => (
                <div
                  key={addOn.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedAddOns.includes(addOn.id)
                      ? "border-[#D4AF37] bg-[#D4AF37]/5"
                      : "border-[#F2EBE5] hover:border-[#D4AF37]/50"
                  }`}
                  onClick={() => handleToggleAddOn(addOn.id)}
                  data-testid={`addon-${addOn.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center mt-1 ${
                          selectedAddOns.includes(addOn.id)
                            ? "border-[#D4AF37] bg-[#D4AF37]"
                            : "border-[#8C8C8C]"
                        }`}
                      >
                        {selectedAddOns.includes(addOn.id) && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg mb-1">{addOn.name}</h4>
                        <p className="text-sm text-[#8C8C8C] mb-2">{addOn.description}</p>
                        
                        {/* Tattoo Cover Quantity */}
                        {addOn.id === "tattoo_cover" && selectedAddOns.includes(addOn.id) && (
                          <div className="mt-3 flex items-center gap-3" onClick={e => e.stopPropagation()}>
                            <Label className="text-sm">Number of areas:</Label>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0"
                                onClick={() => setTattooAreas(Math.max(1, tattooAreas - 1))}
                                data-testid="tattoo-decrease"
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <span className="w-8 text-center font-semibold">{tattooAreas}</span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0"
                                onClick={() => setTattooAreas(tattooAreas + 1)}
                                data-testid="tattoo-increase"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Destination Fee Input */}
                        {addOn.id === "destination" && selectedAddOns.includes(addOn.id) && (
                          <div className="mt-3" onClick={e => e.stopPropagation()}>
                            <Label htmlFor="destination-fee" className="text-sm mb-2 block">
                              Destination fee amount:
                            </Label>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-semibold">$</span>
                              <Input
                                id="destination-fee"
                                type="number"
                                min="0"
                                step="50"
                                value={destinationFee}
                                onChange={e => setDestinationFee(e.target.value)}
                                placeholder="0.00"
                                className="w-32"
                                data-testid="destination-fee-input"
                              />
                            </div>
                            <p className="text-xs text-[#8C8C8C] mt-1">
                              Based on travel distance and logistics
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right ml-4">
                      {addOn.type === "fixed" && (
                        <span className="text-xl font-bold text-[#D4AF37]">
                          +${addOn.price}
                        </span>
                      )}
                      {addOn.type === "quantity" && addOn.id === "tattoo_cover" && (
                        <div>
                          <span className="text-xl font-bold text-[#D4AF37]">
                            +${(addOn.price * (selectedAddOns.includes(addOn.id) ? tattooAreas : 1)).toFixed(2)}
                          </span>
                          <p className="text-xs text-[#8C8C8C] mt-1">${addOn.price} {addOn.unit}</p>
                        </div>
                      )}
                      {addOn.type === "custom" && addOn.id === "destination" && (
                        <span className="text-xl font-bold text-[#D4AF37]">
                          {selectedAddOns.includes(addOn.id) && destinationFee > 0 
                            ? `+$${parseFloat(destinationFee).toFixed(2)}` 
                            : "Custom"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total Summary */}
          <div className="border-t pt-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#8C8C8C]">Base Deposit</span>
                <span className="font-medium">${baseAmount.toFixed(2)}</span>
              </div>
              {addOnsTotal > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#8C8C8C]">Add-Ons</span>
                  <span className="font-medium text-[#D4AF37]">+${addOnsTotal.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-3 border-t">
                <span className="text-xl font-bold">Total Amount</span>
                <span className="text-3xl font-bold text-[#D4AF37]">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="cancel-addons"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              className="flex-1 bg-[#D4AF37] hover:bg-[#b89530] text-white"
              data-testid="confirm-addons"
            >
              Continue to Payment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddOnsModal;